import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, User, Loader2, Plus, MessageSquare, Trash2, Menu, X, AlertCircle, CreditCard, Share2, Download, Copy, Check, Paperclip, FileText, XCircle, ZoomIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import AIConsentModal, { hasAIConsent } from '../components/AIConsentModal';
import { useAuth } from '../contexts/AuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  isStreaming?: boolean;
  attachments?: Array<{ name: string; type: 'image' | 'pdf'; preview?: string }>;
}

interface Conversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessage: {
    role: string;
    content: string;
    createdAt: string;
  } | null;
}

interface UploadedFile {
  id: string;
  file: File;
  preview?: string; // compressed base64 preview for images
  type: 'image' | 'pdf';
}

// Compress image to max 800px and quality 0.7 for preview
const compressImageForPreview = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = document.createElement('img');
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 800;
        let { width, height } = img;
        if (width > height) {
          if (width > MAX_SIZE) { height = Math.round(height * MAX_SIZE / width); width = MAX_SIZE; }
        } else {
          if (height > MAX_SIZE) { width = Math.round(width * MAX_SIZE / height); height = MAX_SIZE; }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

// Simple markdown renderer for AI responses
const renderMarkdown = (text: string) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  
  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const items = listItems.map((item, i) => (
        <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
      ));
      if (listType === 'ul') {
        elements.push(<ul key={elements.length} className="list-disc space-y-1 my-2">{items}</ul>);
      } else {
        elements.push(<ol key={elements.length} className="list-decimal space-y-1 my-2">{items}</ol>);
      }
      listItems = [];
      listType = null;
    }
  };
  
  const formatInline = (line: string): string => {
    return line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-gray-200 px-1 rounded text-sm">$1</code>');
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={elements.length} className="font-bold text-base mt-3 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(line.slice(4)) }} />
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h3 key={elements.length} className="font-bold text-lg mt-3 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(line.slice(3)) }} />
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h2 key={elements.length} className="font-bold text-xl mt-3 mb-1"
            dangerouslySetInnerHTML={{ __html: formatInline(line.slice(2)) }} />
      );
    }
    else if (line.match(/^[\-\*]\s/)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(line.replace(/^[\-\*]\s/, ''));
    }
    else if (line.match(/^\d+\.\s/)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(line.replace(/^\d+\.\s/, ''));
    }
    else if (line.trim() === '') {
      flushList();
      elements.push(<div key={elements.length} className="h-2" />);
    }
    else {
      flushList();
      elements.push(
        <p key={elements.length} className="my-1"
           dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
      );
    }
  }
  
  flushList();
  return <div className="space-y-0">{elements}</div>;
};

// Image lightbox component
const ImageLightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    // Prevent body scroll while lightbox is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-80 transition-colors z-10"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ userSelect: 'none' }}
      />
    </div>
  );
};

// File preview thumbnail component
const FileThumbnail: React.FC<{
  file: UploadedFile;
  onRemove: () => void;
  onPreview?: () => void;
}> = ({ file, onRemove, onPreview }) => (
  <div className="relative flex-shrink-0 group">
    {file.type === 'image' && file.preview ? (
      <div className="relative w-16 h-16 sm:w-20 sm:h-20">
        <img
          src={file.preview}
          alt={file.file.name}
          className="w-full h-full object-cover rounded-xl border-2 border-blue-200 cursor-pointer hover:border-blue-400 transition-colors"
          onClick={onPreview}
        />
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all flex items-center justify-center cursor-pointer"
          onClick={onPreview}
        >
          <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10"
        >
          <X className="w-3 h-3" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 rounded-b-xl px-1 py-0.5">
          <p className="text-white text-[9px] truncate">{file.file.name}</p>
        </div>
      </div>
    ) : (
      <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-red-50 border-2 border-red-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-red-400 transition-colors">
        <FileText className="w-6 h-6 text-red-500" />
        <p className="text-[9px] text-red-600 truncate w-full px-1 text-center">{file.file.name}</p>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    )}
  </div>
);

export const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: _user } = useAuth();
  void _user; // FII FIT: nu mai există distincție CHRONIC vs HEALTHY — toți primesc Nutriționist AI
  const [conversations, setConversations] = useState<Conversation[]>([]);

  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

  // Dispatch event to hide BottomNavigation when sidebar is open on mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: isSidebarOpen } }));
    }
    return () => {
      window.dispatchEvent(new CustomEvent('mobile-drawer-toggle', { detail: { open: false } }));
    };
  }, [isSidebarOpen]);

  const [creditError, setCreditError] = useState<string | null>(null);
  const [showAIConsent, setShowAIConsent] = useState(!hasAIConsent());
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingContentRef = useRef('');

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversations la mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Focus input doar pe desktop
  useEffect(() => {
    if (window.innerWidth >= 768) {
      inputRef.current?.focus();
    }
  }, [currentConversationId]);

  // Cleanup abort controller
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Cleanup previews on unmount to free memory
  useEffect(() => {
    return () => {
      // No object URLs to revoke since we use canvas data URLs
    };
  }, []);

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/conversations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const loadConversation = async (conversationId: string) => {
    try {
      setCurrentConversationId(conversationId);
      setMessages([]);
      setCreditError(null);
      setIsSidebarOpen(false);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/conversations/${conversationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setCreditError(null);
    setUploadedFiles([]);
    setIsSidebarOpen(false);
    if (window.innerWidth >= 768) inputRef.current?.focus();
  };

  // Handle file selection with compression
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Limit to 10 files total
    const remaining = 10 - uploadedFiles.length;
    if (remaining <= 0) {
      alert('Poți încărca maxim 10 fișiere odată.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const filesToProcess = files.slice(0, remaining);

    const newFiles: UploadedFile[] = [];

    for (const file of filesToProcess) {
      const isImage = file.type.startsWith('image/') || 
        ['jpg', 'jpeg', 'png', 'webp', 'gif'].some(ext => file.name.toLowerCase().endsWith(ext));
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

      if (!isImage && !isPdf) {
        alert(`Fișierul "${file.name}" nu este suportat. Acceptăm imagini (JPG, PNG, WEBP) și PDF-uri.`);
        continue;
      }

      if (file.size > 20 * 1024 * 1024) {
        alert(`Fișierul "${file.name}" este prea mare. Limita este 20MB.`);
        continue;
      }

      const uploadedFile: UploadedFile = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        type: isImage ? 'image' : 'pdf'
      };

      if (isImage) {
        try {
          uploadedFile.preview = await compressImageForPreview(file);
        } catch {
          // fallback: read as data URL without compression
          uploadedFile.preview = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.readAsDataURL(file);
          });
        }
      }

      newFiles.push(uploadedFile);
    }

    setUploadedFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAllFiles = () => {
    setUploadedFiles([]);
  };

  // Send files for analysis
  const handleAnalyzeFiles = async () => {
    if (!uploadedFiles.length || isAnalyzing) return;

    setIsAnalyzing(true);
    setCreditError(null);

    // Build user message with previews stored
    const fileNames = uploadedFiles.map(f => f.file.name).join(', ');
    const userMessageContent = input.trim() 
      ? `${input.trim()}\n\n📎 Fișiere atașate: ${fileNames}`
      : `📎 Am încărcat pentru analiză: ${fileNames}`;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessageContent,
      createdAt: new Date().toISOString(),
      attachments: uploadedFiles.map(f => ({
        name: f.file.name,
        type: f.type,
        preview: f.preview // store preview in message for display
      }))
    };

    setMessages(prev => [...prev, userMsg]);

    const assistantId = (Date.now() + 1).toString();
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      isStreaming: true
    }]);

    // Capture current values before clearing state
    const filesToSend = [...uploadedFiles];
    const currentInput = input.trim();

    // Clear files and input immediately for better UX
    setUploadedFiles([]);
    setInput('');

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();

      for (const uploadedFile of filesToSend) {
        formData.append('files', uploadedFile.file);
      }

      if (currentInput) {
        formData.append('message', currentInput);
      }

      if (currentConversationId) {
        formData.append('conversationId', currentConversationId);
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/chat/analyze-media`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.status === 402) {
        const errorData = await response.json();
        setCreditError(errorData.message || 'Credite insuficiente');
        setMessages(prev => prev.filter(m => m.id !== assistantId && m.id !== userMsg.id));
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Eroare server' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantId
            ? { ...msg, content: data.analysis, isStreaming: false }
            : msg
        )
      );

      if (data.conversationId && !currentConversationId) {
        setCurrentConversationId(data.conversationId);
      }

      loadConversations();

    } catch (error: any) {
      console.error('Analysis error:', error);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantId
            ? { ...msg, content: error.message || 'Eroare la analiza documentelor.', isStreaming: false }
            : msg
        )
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSend = async () => {
    if (uploadedFiles.length > 0) {
      await handleAnalyzeFiles();
      return;
    }

    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      createdAt: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setCreditError(null);
    streamingContentRef.current = '';

    const assistantId = (Date.now() + 1).toString();

    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant' as const,
      content: '',
      createdAt: new Date().toISOString(),
      isStreaming: true
    }]);

    try {
      const token = localStorage.getItem('accessToken');
      
      abortControllerRef.current = new AbortController();
      const timeoutId = setTimeout(() => abortControllerRef.current?.abort(), 90000);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/chat/stream-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversationId: currentConversationId || undefined
        }),
        signal: abortControllerRef.current.signal
      });

      clearTimeout(timeoutId);

      if (response.status === 402) {
        const errorData = await response.json();
        setCreditError(errorData.message || 'Credite insuficiente');
        setMessages(prev => prev.filter(m => m.id !== assistantId));
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Eroare server' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) throw new Error('Stream nu este disponibil');

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);

            if (data.error) {
              if (data.code === 'INSUFFICIENT_CREDITS') {
                setCreditError(data.message || 'Credite insuficiente');
                setMessages(prev => prev.filter(m => m.id !== assistantId));
              } else {
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === assistantId
                      ? { ...msg, content: data.message || 'Eroare la streaming', isStreaming: false }
                      : msg
                  )
                );
              }
              setIsLoading(false);
              return;
            }

            if (data.chunk) {
              streamingContentRef.current += data.chunk;
              const currentContent = streamingContentRef.current;
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === assistantId
                    ? { ...msg, content: currentContent }
                    : msg
                )
              );
            }

            if (data.done) {
              setMessages(prev =>
                prev.map(msg =>
                  msg.id === assistantId
                    ? { ...msg, isStreaming: false }
                    : msg
                )
              );

              if (data.conversationId && !currentConversationId) {
                setCurrentConversationId(data.conversationId);
              }
              loadConversations();
              setIsLoading(false);
              return;
            }
          } catch (err) {
            // Ignore parse errors for incomplete chunks
          }
        }
      }

      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantId ? { ...msg, isStreaming: false } : msg
        )
      );
      setIsLoading(false);

    } catch (error: any) {
      console.error('Chat error:', error);
      
      if (error.name === 'AbortError') {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantId
              ? { ...msg, content: streamingContentRef.current || 'Timeout - răspunsul a durat prea mult.', isStreaming: false }
              : msg
          )
        );
      } else {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantId
              ? { ...msg, content: error.message || 'Eroare la trimiterea mesajului.', isStreaming: false }
              : msg
          )
        );
      }
      setIsLoading(false);
    } finally {
      abortControllerRef.current = null;
      if (window.innerWidth >= 768) inputRef.current?.focus();
    }
  };

  const deleteConversation = async (conversationId: string) => {
    if (!confirm('Sigur vrei să ștergi această conversație?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/conversations/${conversationId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      if (currentConversationId === conversationId) {
        startNewConversation();
      }

      await loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Export / Distribuie conversație
  const [copied, setCopied] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const buildConversationText = () => {
    const title = conversations.find(c => c.id === currentConversationId)?.title || 'Conversație Nutriționist AI';
    const date = new Date().toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' });
    let text = `${title}\nData: ${date}\nFII FIT - Nutriționist AI Personal\n${'─'.repeat(50)}\n\n`;
    messages.forEach(msg => {
      const time = new Date(msg.createdAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
      const role = msg.role === 'user' ? '👤 Tu' : '🥗 Nutriționist AI';

      text += `[${time}] ${role}:\n${msg.content}\n\n`;
    });
    return text;
  };


  const handleCopyConversation = async () => {
    const text = buildConversationText();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    setShowExportMenu(false);
  };

  const handleDownloadConversation = () => {
    const text = buildConversationText();
    const title = conversations.find(c => c.id === currentConversationId)?.title || 'conversatie';
    const filename = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.txt`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleShareConversation = async () => {
    const text = buildConversationText();
    const title = conversations.find(c => c.id === currentConversationId)?.title || 'Conversație Nutriționist AI';

    if (navigator.share) {
      await navigator.share({ title, text });
    } else {
      await handleCopyConversation();
    }
    setShowExportMenu(false);
  };

  const isBusy = isLoading || isAnalyzing;
  const canSend = (input.trim().length > 0 || uploadedFiles.length > 0) && !isBusy;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <PageHeader title="Nutriționist AI" />


      {/* Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}

      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden flex h-[calc(100vh-80px)] sm:h-[calc(100vh-200px)] relative">
          
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <div
            className={`${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0 ${
              isSidebarOpen ? 'w-80' : 'md:w-0'
            } fixed md:relative inset-y-0 left-0 z-50 md:z-auto transition-all duration-300 border-r border-gray-200 flex flex-col bg-gray-50 h-full md:h-auto`}
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            {isSidebarOpen && (
              <>
                <div className="p-4 border-b border-gray-200">
                  <button
                    onClick={startNewConversation}
                    className="w-full bg-blue-500 text-white rounded-lg p-3 flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Conversație Nouă
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {isLoadingConversations ? (
                    <div className="text-center text-gray-500 py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      <p className="mt-2">Se încarcă...</p>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Nicio conversație încă</p>
                      <p className="text-sm mt-1">Începe o conversație nouă!</p>
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <div
                        key={conv.id}
                        onClick={() => loadConversation(conv.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                          currentConversationId === conv.id
                            ? 'bg-blue-100 border-2 border-blue-500'
                            : 'bg-white border-2 border-transparent hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">{conv.title}</h4>
                            {conv.lastMessage && (
                              <p className="text-xs text-gray-500 truncate mt-1">
                                {conv.lastMessage.content.substring(0, 60)}...
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">{conv.messageCount} mesaje</p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            
            {/* Header Chat */}
            <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200 flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="text-gray-600 hover:text-gray-900 flex-shrink-0"
              >
                {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h3 className="font-semibold text-gray-900 flex-1 truncate text-sm sm:text-base">
                {currentConversationId
                  ? conversations.find(c => c.id === currentConversationId)?.title || 'Conversație'
                  : 'Conversație Nouă'}
              </h3>

              {messages.length > 0 && (
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Exportă / Distribuie conversația"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Exportă</span>
                  </button>

                  {showExportMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                      <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                        <button onClick={handleCopyConversation} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
                          <span>{copied ? 'Copiat!' : 'Copiază conversația'}</span>
                        </button>
                        <button onClick={handleDownloadConversation} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <Download className="w-4 h-4 text-gray-500" />
                          <span>Descarcă (.txt)</span>
                        </button>
                        <button onClick={handleShareConversation} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                          <Share2 className="w-4 h-4 text-gray-500" />
                          <span>Distribuie</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Credit Error Banner */}
            {creditError && (
              <div className="flex-shrink-0 mx-3 sm:mx-6 mt-3 p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-orange-800">{creditError}</p>
                  <button
                    onClick={() => navigate('/extra-credits')}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-orange-700 hover:text-orange-900 bg-orange-100 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    Cumpără Credite
                  </button>
                </div>
                <button onClick={() => setCreditError(null)} className="text-orange-400 hover:text-orange-600 flex-shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Messages Area - scrollable */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4 min-h-0">
              {messages.length === 0 && !creditError && (
                <div className="text-center py-8 sm:py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-blue-100 rounded-full mb-4">
                    <Bot className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Bună! Sunt Nutriționistul tău AI 🥗💪
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm sm:text-base px-4">
                    Pot răspunde la întrebări despre <strong>nutriție, diete, antrenament și fitness</strong>. Te ajut cu planuri alimentare, calcul de calorii, idei de mese, sfaturi pentru slăbit sau masă musculară și multe altele!
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mb-4 px-4">
                    {['🍎 Nutriție', '🥗 Diete', '💪 Antrenament', '🔥 Slăbit', '🏋️ Masă musculară', '⏱️ Meal prep'].map(topic => (
                      <span key={topic} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium border border-green-200">
                        {topic}
                      </span>
                    ))}
                  </div>
                  <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl px-4 sm:px-5 py-3 text-sm text-green-700">
                    <Paperclip className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Apasă <strong>📎</strong> pentru a încărca poze cu etichete nutriționale sau meniuri</span>
                  </div>
                </div>
              )}


              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center self-end ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gradient-to-br from-green-400 to-blue-500 text-white'
                  }`}>
                    {message.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>

                  {/* Message content */}
                  <div className={`min-w-0 max-w-[82%] sm:max-w-[75%] flex flex-col ${
                    message.role === 'user' ? 'items-end' : 'items-start'
                  }`}>
                    
                    {/* Image attachments - shown as grid above the text bubble */}
                    {message.role === 'user' && message.attachments && message.attachments.length > 0 && (
                      <div className={`mb-1.5 flex flex-wrap gap-1.5 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {message.attachments.map((att, idx) => (
                          att.type === 'image' && att.preview ? (
                            <div
                              key={idx}
                              className="relative cursor-pointer group"
                              onClick={() => setLightboxImage({ src: att.preview!, alt: att.name })}
                            >
                              <img
                                src={att.preview}
                                alt={att.name}
                                className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl border-2 border-blue-300 hover:border-blue-500 transition-colors shadow-sm"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all flex items-center justify-center">
                                <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          ) : (
                            <div key={idx} className="flex items-center gap-1.5 bg-blue-100 text-blue-700 rounded-lg px-2 py-1.5 text-xs">
                              <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate max-w-[100px]">{att.name}</span>
                            </div>
                          )
                        ))}
                      </div>
                    )}

                    {/* Text bubble */}
                    <div
                      className={`px-3 py-2 sm:px-4 sm:py-3 rounded-2xl text-sm sm:text-base break-words ${
                        message.role === 'user'
                          ? 'bg-blue-500 text-white rounded-tr-sm'
                          : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                      }`}
                      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' }}
                    >
                      {message.role === 'assistant' && message.content ? (
                        <div className="break-words text-sm sm:text-base">
                          {renderMarkdown(message.content)}
                          {message.isStreaming && (
                            <span className="inline-block w-0.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-text-bottom" />
                          )}
                        </div>
                      ) : message.role === 'assistant' && !message.content && message.isStreaming ? (
                        <div className="flex items-center gap-2 py-1">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          <span className="text-sm text-gray-500">Analizez documentele...</span>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap break-words text-sm sm:text-base">{message.content}</p>
                      )}
                    </div>

                    <p className="text-xs text-gray-400 mt-1 px-1">
                      {new Date(message.createdAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* File Previews - fixed height, horizontally scrollable */}
            {uploadedFiles.length > 0 && (
              <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50">
                <div className="px-3 sm:px-4 pt-2.5 pb-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" />
                      {uploadedFiles.length} fișier{uploadedFiles.length !== 1 ? 'e' : ''} selectat{uploadedFiles.length !== 1 ? 'e' : ''}
                      {(() => {
                        const imageCount = uploadedFiles.filter(f => f.type === 'image').length;
                        const pdfCount = uploadedFiles.filter(f => f.type === 'pdf').length;
                        if (pdfCount === 0) {
                          return <span className="text-blue-600 font-semibold">· {imageCount} credit{imageCount !== 1 ? 'e' : ''}</span>;
                        } else if (imageCount === 0) {
                          return <span className="text-purple-600 font-semibold">· {pdfCount} PDF × 1-10 credite/pagini</span>;
                        } else {
                          return <span className="text-purple-600 font-semibold">· {imageCount} img + {pdfCount} PDF (1-10 pag/PDF)</span>;
                        }
                      })()}
                    </span>
                    <button
                      onClick={clearAllFiles}
                      className="text-xs text-red-500 hover:text-red-700 transition-colors flex items-center gap-1"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Șterge tot
                    </button>
                  </div>
                  {/* Horizontal scroll for thumbnails */}
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
                       style={{ scrollbarWidth: 'thin' }}>
                    {uploadedFiles.map((uf) => (
                      <FileThumbnail
                        key={uf.id}
                        file={uf}
                        onRemove={() => removeFile(uf.id)}
                        onPreview={uf.preview ? () => setLightboxImage({ src: uf.preview!, alt: uf.file.name }) : undefined}
                      />
                    ))}
                    {/* Add more button */}
                    {uploadedFiles.length < 10 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isBusy}
                        className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 border-2 border-dashed border-blue-300 rounded-xl flex flex-col items-center justify-center gap-1 text-blue-400 hover:border-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50"
                      >
                        <Plus className="w-5 h-5" />
                        <span className="text-[9px]">Adaugă</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white p-2.5 sm:p-4"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.625rem)' }}>
              <div className="flex gap-2 items-end">
                {/* File upload button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isBusy}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isBusy}
                  className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl border-2 flex items-center justify-center transition-colors touch-manipulation ${
                    uploadedFiles.length > 0
                      ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-gray-300 bg-white text-gray-500 hover:border-blue-400 hover:text-blue-500'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Încarcă poze cu etichete nutriționale, meniuri sau PDF-uri"

                >
                  {uploadedFiles.length > 0 ? (
                    <span className="text-xs font-bold text-blue-600">{uploadedFiles.length}</span>
                  ) : (
                    <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>

                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    uploadedFiles.length > 0
                      ? 'Adaugă un mesaj opțional...'
                      : 'Întreabă despre nutriție, antrenament sau diete...'
                  }

                  className="flex-1 resize-none rounded-xl border border-gray-300 px-3 sm:px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  style={{ fontSize: '16px', minHeight: '44px', maxHeight: '100px' }}
                  rows={1}
                  disabled={isBusy}
                />

                <button
                  onClick={handleSend}
                  disabled={!canSend}
                  className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl text-white disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center touch-manipulation ${
                    uploadedFiles.length > 0
                      ? 'bg-purple-500 hover:bg-purple-600'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                  title={uploadedFiles.length > 0 ? 'Analizează cu GPT-4.1 Vision' : 'Trimite mesaj'}
                >
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  ) : uploadedFiles.length > 0 ? (
                    <Bot className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5 text-center leading-tight">
                ⚠️ Răspunsurile AI au caracter informativ și educațional. Pentru probleme medicale, consultă un medic sau nutriționist clinician.
              </p>

            </div>
          </div>
        </div>
      </div>

      {/* AI Consent Modal */}
      <AIConsentModal
        isOpen={showAIConsent}
        onAccept={() => setShowAIConsent(false)}
        onDecline={() => navigate('/dashboard')}
      />
    </div>
  );
};
